import { useState, type FormEvent } from "react";
import { type AxiosResponse } from "axios";
import { Button } from "../../../../components/common/button";
import { Input } from "../../../../components/common/input";
import { handleAxiosError } from "../../../../common/error_handling";
import {
  OTP_LENGTH,
  isCompleteOtp,
  sanitizeOtp,
} from "../../../../utils/validation";

type VerifyOtpFormProps<T> = {
  phoneNumber: string;
  onSuccess: (response: T) => void;
  confirmOtpMethod: (
    otp: string,
    phoneNumber: string
  ) => Promise<AxiosResponse<T>>;
  buttonText?: string;
  isResendDisabled: boolean;
  countdown: number;
  onResend: () => void;
};

export function VerifyOtpForm<T>({
  phoneNumber,
  onSuccess,
  confirmOtpMethod,
  buttonText = "Verify OTP",
  isResendDisabled,
  countdown,
  onResend,
}: VerifyOtpFormProps<T>) {
  const [otp, setOtp] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!isCompleteOtp(otp) || isLoading) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await confirmOtpMethod(otp, phoneNumber);
      setSuccess(true);
      onSuccess(response.data);
    } catch (err) {
      // Shown under the field (not as a toast) so the user can correct the code in place.
      setError(handleAxiosError(err).message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      noValidate
      className="flex flex-col items-center w-full space-y-4"
    >
      <div className="w-full">
        <Input
          type="text"
          name="otp"
          inputMode="numeric"
          autoComplete="one-time-code"
          placeholder={`Enter the ${OTP_LENGTH}-digit OTP`}
          value={otp}
          onChange={(e) => {
            setOtp(sanitizeOtp(e.target.value));
            if (error) setError(null);
          }}
          error={error ?? undefined}
          required
        />
      </div>

      <div className="text-center text-sm">
        <button
          className={`inline-flex space-x-2 ${
            isResendDisabled ? "text-textSecondary cursor-not-allowed" : ""
          }`}
          type="button"
          onClick={onResend}
          disabled={isResendDisabled}
        >
          <span>Resend Code {isResendDisabled && `(${countdown}s)`}</span>
        </button>
      </div>

      <div className="w-full">
        <Button variant="primary"
          className="w-full"
          disabled={isLoading || success || !isCompleteOtp(otp)}
          type="submit"
        >
          {isLoading ? "Verifying..." : success ? "Verified" : buttonText}
        </Button>
      </div>
    </form>
  );
}

export default VerifyOtpForm;
